import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Model, { helper } from "@tripian/model";
import { api } from "@tripian/core";
import { SocialLogin } from "@tripian/react";
import ReCAPTCHA from "react-google-recaptcha";

import {
  LOGIN,
  REGISTER,
  TRIPS,
  TRIP_CLONE,
  DELETE_USER,
  TRIP_PLAN,
  VISA_AUTH_REDIRECT,
} from "../../constants/ROUTER_PATH_TITLE";
import { saveLocalStorageToken } from "../../App/AppWrapper/localStorages";

import useAuth from "../../hooks/useAuth";
import useUser from "../../hooks/useUser";
import useTranslate from "../../hooks/useTranslate";
import { useGuestUser } from "../../hooks/useGuestUser";
import { useLocalStorage } from "../../hooks/useLocalStorage";

import TripianLogo from "./img/login_icon.png";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const ValaiLogin: React.FC = () => {
  const { isLoggedIn, loggedIn } = useAuth();
  const { user, userFetch } = useUser();
  const { t } = useTranslate();
  const isGuest = useGuestUser();

  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [recaptchaVerified, setRecaptchaVerified] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string>();
  const [isRecaptchaActive, setIsRecaptchaActive] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const showCaptcha = process.env.NODE_ENV === "production";
  const reCaptchaSiteKey = window.tconfig.RECAPTCHA_SITE_KEY;

  const useQuery = <T extends { [K in keyof T]?: string } = {}>(): T => {
    const query = new URLSearchParams(location.search);
    const result: T = {} as T;
    query.forEach((val, key) => {
      (result as Record<string, string>)[key] = val;
    });
    return result;
  };
  const { sharedTripHash } = useQuery<{ sharedTripHash: string | undefined }>();

  document.title = LOGIN.TITLE(t("user.login"));

  useEffect(() => {
    if (isGuest && user?.userType === -1) {
      return;
    }

    if (!isLoggedIn) {
      return;
    }

    // User is logged in
    if (!user) return;

    const isVisaFlow =
      window.tconfig.ELIGIBILITY_TYPE === "visa" &&
      user.userType !== Model.USER_TYPE.ELIGIBILITY;

    // If there is visa flow, redirect to VisaAuthRedirect page
    if (isVisaFlow) {
      const queryParams = sharedTripHash
          ? `?sharedTripHash=${sharedTripHash}`
          : "";
      navigate(`${VISA_AUTH_REDIRECT.PATH}${queryParams}`, {replace: true});
      return;
    }

    const fromTripPlan: boolean | undefined =
      location.state &&
      (location.state as { fromTripPlan?: boolean })?.fromTripPlan;
    const tripHash: string | undefined =
      location.state && (location.state as { tripHash?: string })?.tripHash;

    if (sharedTripHash) {
      navigate(`${TRIP_CLONE.PATH}/${sharedTripHash}`, { replace: true });
    } else if (
      location.state &&
      (location.state as { from: string })?.from === DELETE_USER.PATH
    ) {
      navigate(DELETE_USER.PATH, { replace: true });
    } else if (fromTripPlan && tripHash) {
      navigate(`${TRIP_PLAN.PATH}/${tripHash}/0`, { replace: true });
    } else {
      navigate(TRIPS.PATH, { replace: true });
    }
  }, [isLoggedIn, user, sharedTripHash, location, navigate, isGuest]);

  const isValid = () => {
    const emailValid = helper.emailFormatValid(email);
    let newWarningMessage = "";

    if (!emailValid && email !== "") {
      newWarningMessage = "Email format is not valid";
    } else if (password.length < 8) {
      newWarningMessage = "Password needs to be at least 8 characters long";
    } else if (email === "") {
      newWarningMessage = "Email can't be empty";
    }

    setWarningMessage(newWarningMessage);
    if (newWarningMessage !== "") {
      setIsLoading(false);
    }
    return newWarningMessage === "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(undefined);

    if (recaptchaVerified || !showCaptcha || !isRecaptchaActive) {
      if (isValid()) {
        setIsLoading(true);
        try {
          // Only send the token when the captcha has actually been solved; never send an empty string.
          const token = await api.loginEmail(
            email,
            password,
            undefined,
            captchaToken || undefined
          );
          saveLocalStorageToken(token);
          loggedIn();
          userFetch(true);
        } catch (err: unknown) {
          if (err instanceof Error) {
            setError(err.message);
          }
          setIsRecaptchaActive(true);
          setIsLoading(false);
          // reCAPTCHA tokens are single-use; force a fresh solve before the next attempt.
          setRecaptchaVerified(false);
          setCaptchaToken(undefined);
          recaptchaRef.current?.reset();
        }
      }
    } else {
      setWarningMessage("Please verify that you are not a robot.");
    }
  };

  const verifyCallback = (token: string | null) => {
    if (token) {
      setRecaptchaVerified(true);
      setCaptchaToken(token);
    } else {
      // Token expired or was cleared by the widget.
      setRecaptchaVerified(false);
      setCaptchaToken(undefined);
    }
  };

  const socialLoginConfigs = {
    clientId: window.tconfig.COGNITO.CLIENT_ID,
    domain: window.tconfig.COGNITO.DOMAIN,
    identityProviders: window.tconfig.COGNITO.IDENTITY_PROVIDERS,
    region: window.tconfig.COGNITO.REGION,
    baseDomain: `${window.tconfig.DOMAIN_ORIGIN}${window.tconfig.DOMAIN_ROUTER_BASE_NAME}`,
  };

  const handleSignUp = () => {
    if (sharedTripHash) {
      navigate(`/register?sharedTripHash=${sharedTripHash}`, { replace: true });
    } else if (isGuest) {
      navigate(REGISTER.PATH, { replace: true, state: { fromCloneModal: true } });
    } else {
      navigate("/register", { replace: true });
    }
  };

  const { getLocalData } = useLocalStorage<"light" | "dark">("theme");
  const theme = getLocalData();

  if (isLoggedIn && !isGuest) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Navbar */}
      <Navbar theme={theme} t={t} />

      {/* Main Content */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-4 pt-14-h pb-12"
        onKeyDown={(e) => e.key === "Enter" && !isLoading && handleSubmit(e as unknown as React.FormEvent)}
        role="button"
        tabIndex={0}
      >
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src={TripianLogo}
              alt="Logo"
              className="h-12"
            />
          </div>

          {/* Card */}
          <div className="rounded-2xl relative">
          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-2xl z-10">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2 syne-fm">
              {t("valai.loginTitle")}
            </h1>
            <p className="text-gray-500">
              {t("valai.loginSubtitle")}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("auth.login.email.label")}
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())}
                placeholder={t("auth.login.email.placeholder")}
                autoComplete="username"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("auth.login.password.label")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value.trim())}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  {t("valai.rememberFor30Days")}
                </span>
              </label>
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {t("auth.login.forgotPassword")}
              </button>
            </div>

            {/* ReCAPTCHA */}
            {showCaptcha && isRecaptchaActive && (
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  badge="inline"
                  sitekey={reCaptchaSiteKey}
                  onChange={verifyCallback}
                />
              </div>
            )}

            {/* Error & Warning Messages */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                {error}
              </div>
            )}
            {warningMessage && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm text-center">
                {warningMessage}
              </div>
            )}

            {/* Sign in Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#2563EB] text-white font-medium py-3 border-radius-56-c  hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("valai.login")}
            </button>
          </form>

          {/* Social Login */}
          {(socialLoginConfigs.identityProviders?.includes("Google") || socialLoginConfigs.identityProviders?.includes("SignInWithApple")) && (
            <>
              <div className="flex items-center my-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-4 text-sm text-gray-500">{t("auth.login.or")}</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>
              <SocialLogin configs={socialLoginConfigs} customQuery={sharedTripHash} t={t} />
            </>
          )}

          {/* Sign up link */}
          {window.tconfig.SHOW_REGISTER && (
            <p className="mt-6 text-center text-sm text-gray-600">
              {t("auth.login.account")}{" "}
              <button
                type="button"
                onClick={handleSignUp}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                {t("valai.signup")}
              </button>
            </p>
          )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer theme={theme} t={t} />
    </div>
  );
};

export default ValaiLogin;